import * as React from 'react';
import { useSelector } from 'react-redux';

import { SliderModalLoadable, TSliderModalProps } from '../../CompareBannerContainer/loadable';
import { getUtmSource } from '../../../selectors/utmSource';
import { useApplicationContext } from '../../../utils/applicationContext';
import {
  COMPARE_ENABLED_CONFIG,
  COMPARE_FLIGHT_SLIDES_CONFIG,
  COMPARE_FLIGHT_TRAP_ENABLED_CONFIG,
  COMPARE_FLIGHT_UTM_SOURCE,
} from '../constants/compare';
import { CompareButtonOnboarding } from '../CompareButtonOnboarding';
import { CompareButtonWithTooltip } from '../CompareButtonWithTooltip';
import { CompareFlightButtonOnboarding, useCompareFlightOnboarding } from '../CompareFlightOnboardingTooltip';
import { useCompareOnboarding } from '../hooks/useCompareOnboarding';

type TCompareFlightSlides = TSliderModalProps['slides'];

export interface ICompareButtonWithOnboardingContainerProps {
  isAdded: boolean;
  isVisible: boolean;
  onClick(): void;
  withOnboarding: boolean;
}

export const CompareButtonWithOnboardingContainer = ({
  isAdded,
  isVisible,
  onClick,
  withOnboarding,
}: ICompareButtonWithOnboardingContainerProps) => {
  const { config } = useApplicationContext();
  const utmSource = useSelector(getUtmSource);
  const [compareFlightModalOpen, setCompareFlightModalOpen] = React.useState(false);
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isCompareFlightEnabled = config.get<boolean>(COMPARE_FLIGHT_TRAP_ENABLED_CONFIG) || false;
  const compareFlightSlides = config.get<TCompareFlightSlides>(COMPARE_FLIGHT_SLIDES_CONFIG) ?? [];

  const isCompareFlightOnboardingAvailable =
    withOnboarding && isCompareFlightEnabled && utmSource === COMPARE_FLIGHT_UTM_SOURCE;
  const { open, closeHandler } = useCompareOnboarding(
    withOnboarding && isCompareEnabled && !isCompareFlightOnboardingAvailable,
  );
  const { open: compareFlightOpen, closeHandler: compareFlightCloseHandler } = useCompareFlightOnboarding(
    isCompareFlightOnboardingAvailable,
  );

  const handleCompareFlightModalOpen = React.useCallback(() => {
    compareFlightCloseHandler();
    setCompareFlightModalOpen(true);
  }, [compareFlightCloseHandler]);

  const handleCompareFlightModalClose = React.useCallback(() => {
    setCompareFlightModalOpen(false);
  }, []);

  if (!isVisible) {
    return null;
  }

  const button = <CompareButtonWithTooltip isAdded={isAdded} onClick={onClick} />;

  const buttonWithOnboarding = !withOnboarding ? (
    button
  ) : compareFlightOpen ? (
    <CompareFlightButtonOnboarding
      open={compareFlightOpen}
      onClose={compareFlightCloseHandler}
      onAboutComparisonClick={handleCompareFlightModalOpen}
    >
      {button}
    </CompareFlightButtonOnboarding>
  ) : open ? (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      {button}
    </CompareButtonOnboarding>
  ) : (
    button
  );

  return (
    <>
      {buttonWithOnboarding}
      {compareFlightModalOpen && (
        <SliderModalLoadable slides={compareFlightSlides} onClose={handleCompareFlightModalClose} />
      )}
    </>
  );
};
